<template>
  <div id="tract-visit-gpx-map-container" style="position: relative; width: 100%;" :style="{ height }">
    <MapLayerControls :map="map" :tract="tract" :layerCeiling="layerCeiling"/>
    <div :id="mapId" style="height: 100%; width: 100%;"></div>
  </div>
</template>

<script>
import * as atlas from 'azure-maps-control'
import { mapActions, mapGetters } from 'vuex'
import { theme } from '@/plugins/theme'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { angelGeojsonClient, AngelGeojsonLayer } from '@/utils/map/Geojson.js'
import { harvestedProgress, tractMarkerSvg, tractPopup } from '@/utils/map/TractMarkers'
import {
  mapResizeTimeout, defaultCoordinates, azureMapsApiKey, createPolygonLayersForDataSource,
  PolygonStyle, zoomToFeature, disposeMap
} from '@/utils/map/MapHelpers'

const CUT = AngelGeojsonLayer.Cut
const PARCELS = AngelGeojsonLayer.Parcels

export default {
  name: 'TractVisitGpxMap',

  components: {
    MapLayerControls: () => import('@/components/maps/MapLayerControls.vue')
  },

  props: {
    tract: {
      type: Object,
      required: true
    },
    features: {
      type: Object,
      required: true
    },
    height: {
      type: String,
      default: '70vh'
    }
  },

  data: () => ({
    mapId: uniqueDialogId('tract-visit-gpx-map'),
    map: null,
    layerCeiling: 'labels'
  }),

  computed: {
    ...mapGetters('tract-hud', ['allTractHudSections'])
  },

  watch: {
    '$vuetify.breakpoint.width' () { this.resizeMap() },
    '$vuetify.breakpoint.height' () { this.resizeMap() }
  },

  created () {
    this.pinnedLayers = []
    this.tractMarker = null
    this.popupJustToggled = false
  },

  mounted () {
    this.$nextTick(() => this.initMap())
  },

  beforeDestroy () {
    if (this.map) {
      disposeMap(this.map)
      this.map = null
    }
  },

  methods: {
    ...mapActions('tract-hud', ['fetchTractHudSections']),

    initMap () {
      this.map = new atlas.Map(this.mapId, {
        center: [this.tract.spot?.longitude ?? defaultCoordinates.longitude, this.tract.spot?.latitude ?? defaultCoordinates.latitude],
        zoom: 13,
        view: 'Auto',
        style: 'satellite_road_labels',
        authOptions: { authType: 'subscriptionKey', subscriptionKey: azureMapsApiKey() }
      })
      this.map.events.add('ready', this.onMapReady)
    },

    async onMapReady () {
      const zoomTargets = [...this.features.features]

      if (this.tract.spot) {
        if (!this.allTractHudSections) {
          try {
            await this.fetchTractHudSections()
          } catch (e) {
            console.error(e)
          }
        }
        if (!this.map) return
        const position = [this.tract.spot.longitude, this.tract.spot.latitude]
        const progress = harvestedProgress(this.tract)
        const marker = new atlas.HtmlMarker({
          position,
          htmlContent: tractMarkerSvg(this.tract, progress),
          popup: tractPopup(this.tract, progress)
        })
        this.map.events.add('click', marker, () => {
          marker.togglePopup()
          this.popupJustToggled = true
        })
        this.map.events.add('click', this.closeTractPopup)
        this.map.markers.add(marker)
        this.tractMarker = marker
        zoomTargets.push(new atlas.data.Point(position))
      }

      const angelGeojson = await angelGeojsonClient.readGeojson(this.tract.tractId)
      if (!this.map) return
      for (const [layer, style] of [[PARCELS, PolygonStyle.Parcels], [CUT, PolygonStyle.Cut]]) {
        if (!angelGeojson?.layerIsNotEmpty(layer)) continue
        const source = new atlas.source.DataSource()
        this.map.sources.add(source)
        source.setShapes(angelGeojson.getLayer(layer))
        this.pinnedLayers.push(...createPolygonLayersForDataSource(source, style))
        zoomTargets.push(...angelGeojson.getFeatures(layer))
      }

      const gpxSource = new atlas.source.DataSource()
      this.map.sources.add(gpxSource)
      gpxSource.add(this.features)
      this.pinnedLayers.push(
        new atlas.layer.LineLayer(gpxSource, null, {
          strokeColor: theme.primary,
          strokeWidth: 3,
          filter: ['==', ['geometry-type'], 'LineString']
        }),
        new atlas.layer.BubbleLayer(gpxSource, null, {
          color: theme.primary,
          strokeColor: '#FFFFFF',
          strokeWidth: 2,
          radius: 6,
          filter: ['==', ['geometry-type'], 'Point']
        })
      )
      this.map.layers.add(this.pinnedLayers, 'labels')
      this.layerCeiling = this.pinnedLayers[0].getId()

      zoomToFeature(new atlas.data.FeatureCollection(zoomTargets), this.map, { type: 'jump' })
      this.resizeMap()
    },

    closeTractPopup () {
      if (this.popupJustToggled) {
        this.popupJustToggled = false
        return
      }
      if (this.tractMarker) this.tractMarker.options.popup.remove()
    },

    resizeMap () {
      if (!this.map) return
      setTimeout(() => {
        if (this.map) this.map.map.resize()
      }, mapResizeTimeout)
    }
  }
}
</script>

<style src="@/styles/map-global.css"/>
